import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useI18n, useSelector } from '~Hooks';
import { gql, useQuery } from '@apollo/client';
import { GetInventoryForAllVenues, GetInventoryForAllVenuesVariables } from './__queries__';
import { useInventoryApi } from '~Components/InventoryUniverse';
import QtyChanger from '~Components/QtyChanger';
import Button from '~Components/Button';

const Content = styled.div`
  padding: 0 48px 24px;
`;

const ConfirmWrapper = styled.div`
  margin: auto 0;
  animation: fade-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  padding: 16px;
  margin-top: 40px;
  background-color: #fff;

  div:nth-child(2) {
    flex: 1;
    margin-left: 16px;
  }
`;

const Table = styled.div`
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  background-color: #fff;
  height: auto;
  max-height: 400px;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  box-sizing: border-box;
`;

const TableHeader = styled.div`
  display: flex;
  background-color: #fbfbfb;
  align-items: center;
  border-bottom: 1px solid #e8e9ea;
`;

const Body = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding-bottom: 8px;
`;

const Row = styled.div<{ selected: boolean }>`
  background-color: #fff;
  width: 100%;
  display: flex;
  box-sizing: border-box;
  position: relative;
  z-index: ${(props) => (props.selected ? '1' : '0')};
  transition: box-shadow 2s ease-in-out;
  box-shadow: ${(props) => (props.selected ? '0px 1px 3px 0px rgba(0, 0, 0, 0.24)' : 'none')};
  border: 1px solid;
  border-color: ${(props) => (props.selected ? '#004149' : '#e8e9ea')};
  margin-top: -1px;
  transition: border-color 0.2s ease-in-out;

  &:first-child {
    margin-top: 0;
  }

  &:nth-of-type(2n) {
    background-color: #fbfbfb;
  }

  div {
    font-weight: ${(props) => (props.selected ? '600' : '400')};
  }
`;

const Title = styled.div`
  flex: 1;
  color: #41474f;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  padding: 18px 24px;
  box-sizing: border-box;
  text-align: left;
`;

const QtyTitle = styled.div`
  width: 156px;
  text-align: center;
  color: #41474f;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  padding: 18px 8px;
  box-sizing: border-box;
`;

const VenueName = styled.div`
  flex: 1;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.24px;
  color: #1c232c;
  line-height: 20px;
  box-sizing: border-box;
  word-break: break-word;
  text-align: left;
`;

const VenueCount = styled.div<{ selected: boolean }>`
  padding: 14px 8px;
  width: 156px;
  box-sizing: border-box;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  border-left: 1px solid;
  border-color: ${(props) => (props.selected ? '#004149' : '#e8e9ea')};
  position: relative;
  color: #1c232c;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  transition: border-color 0.2s ease-in-out;
`;

const QtyWrapper = styled.div`
  margin: auto 0;
`;

const ConfirmTitle = styled.div`
  color: #41474f;
  font-size: 20px;
  letter-spacing: 0.21px;
  line-height: 20px;
  margin-bottom: 16px;
`;

const ConfirmValue = styled.div`
  color: #41474f;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 0.36px;
  line-height: 24px;
  margin-bottom: 36px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ALL_VENUES_INVENTORY_QUERY = gql`
  query GetInventoryForAllVenues($companyId: ID!) {
    venues: allVenues(filter: { deleted: false, active: true, company: { id: $companyId } }) {
      id
      name

      inventory {
        snapshot
      }
    }
  }
`;

interface Props {
  venueId: string;
  itemId: string;
  itemName: string;
  itemQty: number;
  goBack: () => void;
  handleClose: () => void;
}

interface InventoryVenue {
  id: string;
  name: string;
  count: number | null;
}

const MoveStock: React.FunctionComponent<Props> = ({
  venueId,
  itemName,
  itemId,
  itemQty,
  goBack,
  handleClose,
}) => {
  const { i18n } = useI18n();
  const companyId = useSelector((state) => state.company);
  const inventory = useInventoryApi(venueId);
  const [count, setCount] = useState(0);
  const [selectedVenue, setSelectedVenue] = useState<string | null>(null);
  const [showConfirmStep, setShowConfirmStep] = useState(false);

  const { data, loading } = useQuery<GetInventoryForAllVenues, GetInventoryForAllVenuesVariables>(
    ALL_VENUES_INVENTORY_QUERY,
    {
      variables: {
        companyId,
      },
    },
  );

  const inventoryForVenue = (data?.venues || []).reduce((arr, venue) => {
    const item = venue.inventory?.snapshot?.items.find((item: any) => item.itemId === itemId);

    arr.push({
      id: venue.id,
      name: venue.name,
      count: item ? item.count : null,
    });

    return arr;
  }, [] as InventoryVenue[]);

  // Autoselect first venue
  useEffect(() => {
    const firstVenue = inventoryForVenue.filter((item) => item.id !== venueId)[0];
    if (!firstVenue) return;

    setSelectedVenue(firstVenue.id);
  }, []);

  // Change count to 0 when changing venue
  useEffect(() => setCount(0), [selectedVenue]);

  if (loading || !data) {
    return <h2>Loading</h2>;
  }

  const selectedInventory = inventoryForVenue.find((venue) => venue.id === selectedVenue);

  function handleCountChange(number: number) {
    if (!number) {
      setCount(0);
    } else {
      setCount(Math.max(Math.min(itemQty, number), 0));
    }
  }

  function handleConfirmMove() {
    if (!selectedVenue) return;
    inventory.moveInventory(itemId, count, selectedVenue);
    handleClose();
  }

  return (
    <>
      {!showConfirmStep ? (
        <>
          <Content>
            <Table>
              <TableHeader>
                <Title>{i18n('Inventory.Venue')}</Title>
                <QtyTitle>{i18n('Inventory.CurrentStock')}</QtyTitle>
              </TableHeader>
              <Body>
                {inventoryForVenue
                  .filter((venue) => venue.id !== venueId)
                  .map((venue) => (
                    <Row
                      key={venue.id}
                      onTouchStart={() => setSelectedVenue(venue.id)}
                      selected={selectedVenue === venue.id}>
                      <VenueName>{venue.name}</VenueName>
                      <VenueCount selected={selectedVenue === venue.id}>{venue.count}</VenueCount>
                    </Row>
                  ))}
              </Body>
            </Table>
          </Content>

          <QtyWrapper>
            <QtyChanger
              defaultValue={count}
              handleInput={(val) => handleCountChange(val)}
              handleDecrease={() => handleCountChange(count - 1)}
              handleIncrease={() => handleCountChange(count + 1)}
              showMax={itemQty}
            />
          </QtyWrapper>

          <ButtonsWrapper>
            <Button
              handleClick={goBack}
              text={i18n('Inventory.Back')}
              width="200px"
              textColor="#2f353d"
              background="transparent"
              border="1px solid #1c232c"
            />
            <Button
              text={i18n('Inventory.Continue')}
              handleClick={() => setShowConfirmStep(true)}
              disabled={!selectedVenue || count < 1}
            />
          </ButtonsWrapper>
        </>
      ) : (
        <>
          <ConfirmWrapper>
            <ConfirmTitle>{i18n('Inventory.ChosenItem')}</ConfirmTitle>
            <ConfirmValue>{itemName}</ConfirmValue>
            <ConfirmTitle>{i18n('Inventory.TargetVenue')}</ConfirmTitle>
            <ConfirmValue>{selectedInventory!.name}</ConfirmValue>
            <ConfirmTitle>{i18n('Inventory.QuantityToMove')}</ConfirmTitle>
            <ConfirmValue>{count}</ConfirmValue>
          </ConfirmWrapper>

          <ButtonsWrapper>
            <Button
              handleClick={() => setShowConfirmStep(false)}
              text={i18n('Inventory.Back')}
              width="200px"
              textColor="#2f353d"
              background="transparent"
              border="1px solid #1c232c"
            />
            <Button
              text={i18n('Inventory.Confirm')}
              handleClick={handleConfirmMove}
              disabled={!selectedVenue}
            />
          </ButtonsWrapper>
        </>
      )}
    </>
  );
};

export default MoveStock;
